import type {
  IndustrySetupContext,
  IndustrySetupInput,
  IndustrySetupSelections,
} from "./types";

type SetupOption = {
  uuid: string;
  name: string;
  parentUuid?: string | null;
  companyUuid?: string | null;
  branchUuid?: string | null;
  resourceUuids?: string[];
};

export function chooseExistingSetupOption(
  options: readonly SetupOption[],
  selectedUuid?: string | null,
): string | null {
  if (selectedUuid && options.some((option) => option.uuid === selectedUuid)) return selectedUuid;
  return options.length === 1 ? options[0].uuid : null;
}

export function preparePersonalTrainerInput(input: IndustrySetupInput): IndustrySetupInput {
  return input.installOnly
    ? { ...input, defaultDurationMinutes: undefined, personalTrainerSetup: undefined }
    : { ...input, ...input.personalTrainerSetup ? { defaultDurationMinutes: undefined } : {} };
}

export function resolvePersonalTrainerSelections(
  context: IndustrySetupContext,
  input: IndustrySetupInput,
) {
  const companyUuid = chooseExistingSetupOption(context.companies, input.companyUuid ?? context.companyUuid);
  const branches = companyUuid
    ? context.branches.filter((branch) => branch.parentUuid === companyUuid)
    : [];
  const branchUuid = chooseExistingSetupOption(branches, input.branchUuid ?? context.branchUuid);
  const inScope = (option: SetupOption) => (
    (!option.companyUuid || option.companyUuid === companyUuid)
    && (!option.branchUuid || option.branchUuid === branchUuid)
  );
  const coachProfiles = context.coachProfiles.filter(inScope);
  const coachProfileUuid = input.installOnly
    ? null
    : chooseExistingSetupOption(coachProfiles, input.coachProfileUuid ?? context.coachProfileUuid);
  const profile = coachProfiles.find((item) => item.uuid === coachProfileUuid);
  const scopedBookingPortals = context.bookingPortals.filter((portal) => (
    !portal.resourceUuids?.length
    || context.bookingResources.some((resource) => portal.resourceUuids.includes(resource.uuid) && inScope(resource))
  ));
  const bookingPortals = profile?.parentUuid
    ? scopedBookingPortals.filter((portal) => portal.uuid === profile.parentUuid)
    : scopedBookingPortals;
  const bookingPortalUuid = input.installOnly
    ? null
    : chooseExistingSetupOption(bookingPortals, profile?.parentUuid ?? input.bookingPortalUuid ?? context.bookingPortalUuid);
  const bookingResources = bookingPortalUuid
    ? context.bookingResources.filter((item) => item.parentUuid === bookingPortalUuid && inScope(item))
    : [];
  const bookingResourceUuid = chooseExistingSetupOption(bookingResources, input.bookingResourceUuid ?? context.bookingResourceUuid);
  const bookingServices = bookingPortalUuid
    ? context.bookingServices.filter((item) => (
      item.parentUuid === bookingPortalUuid && inScope(item)
      && (!item.resourceUuids?.length || (bookingResourceUuid !== null && item.resourceUuids.includes(bookingResourceUuid)))
    ))
    : [];
  const bookingServiceUuid = chooseExistingSetupOption(bookingServices, input.bookingServiceUuid ?? context.bookingServiceUuid);
  const clientPortalEnabled = !input.installOnly && (
    input.selectedRecommendedAppCodes == null
    || input.selectedRecommendedAppCodes.includes("client-portal")
    || context.clientPortals.length > 0
  );
  const clientPortalUuid = clientPortalEnabled
    ? chooseExistingSetupOption(context.clientPortals, input.clientPortalUuid ?? context.clientPortalUuid)
    : null;
  const selections: IndustrySetupSelections = {
    companyUuid,
    branchUuid,
    coachProfileUuid,
    bookingPortalUuid,
    bookingResourceUuid,
    bookingServiceUuid,
    clientPortalUuid,
  };
  const groups: { field: keyof IndustrySetupSelections; options: SetupOption[]; value: string | null }[] = [
    { field: "companyUuid", options: context.companies, value: companyUuid },
    { field: "branchUuid", options: branches, value: branchUuid },
  ];
  if (!input.installOnly) {
    groups.push(
      { field: "coachProfileUuid", options: coachProfiles, value: coachProfileUuid },
      { field: "bookingPortalUuid", options: bookingPortals, value: bookingPortalUuid },
      { field: "bookingResourceUuid", options: bookingResources, value: bookingResourceUuid },
      { field: "bookingServiceUuid", options: bookingServices, value: bookingServiceUuid },
    );
    if (clientPortalEnabled) {
      groups.push({ field: "clientPortalUuid", options: context.clientPortals, value: clientPortalUuid });
    }
  }

  return {
    selections,
    groups,
    needsSelection: groups.some((group) => group.options.length > 1 && group.value === null),
  };
}
